// Copyright (c) Ping. All rights reserved.
//
// Shared in-memory projection of in-conversation game sessions.
//
// A "game session" is the fold of a stream of game envelopes
// (create / join / move / reveal / outcome / abort) that share a
// `session_id`. This hook keeps that fold in React state, keyed by the
// conversation it belongs to, and hands the host two builder helpers so
// it can produce the envelope bodies to `conv.send`.
//
// Scope (v1):
//   - No Dexie table. The projection is purely in-memory and rebuilt
//     from whatever events the host feeds in (live `onMessage` and/or a
//     replay of persisted rows it already holds). Durable persistence
//     of the projection — a `game_sessions` table fed from the
//     `game_moves` rows defined in docs/architecture/types-and-schemas.md
//     §-storage — is a deliberate follow-up.
//   - Correctness is targeted at the immediate-resolve / two-move games
//     (coinflip, dice, rps). chess / blackjack / racing ride the exact
//     same create + move + outcome plumbing; their full rules live in
//     their respective hosts and are out of scope for the projection.
//
// On-wire note: the envelope JSON wrapper carries binary fields
// (`session_id`, `payload`, `commit`, `salt`, `value`) as lowercase hex
// strings, NOT raw Uint8Array — see the `read*` decoders in
// packages/ui/src/appEvent/envelope.ts. The builder helpers below
// therefore emit hex strings, and `GameMove` stores hex strings.

import { useCallback, useMemo, useRef, useState } from "react";

// GameKind mirrors the protocol union (types-and-schemas.md §2.9). We
// import it from the picker overlay, which owns the canonical local
// definition until the appEvent registry exports its own. The two are
// structurally identical.
import type { GameKind } from "../chat/games/GamePickerOverlay";
import { parseEnvelope } from "../appEvent/envelope";
import type {
	GameCreateBody,
	GameJoinBody,
	GameMoveBody,
	GameRevealBody,
	GameOutcomeBody,
	GameAbortBody,
} from "../appEvent/types";

export type { GameKind };

/** Lifecycle phase of a single game session. */
export type GamePhase = "open" | "in_progress" | "finished" | "aborted";

/** One projected move within a session. `payload` is hex (on-wire form). */
export interface GameMove {
	readonly moveIdx: number;
	/** Player who made the move (user_id). Optional for system/derived moves. */
	readonly by?: string;
	/** Game-kind-specific encoding, hex. */
	readonly payload: string;
	/** Optional commit hash (hex) for hidden-state / commit-reveal games. */
	readonly commit?: string;
}

/** Terminal result of a session, present once `phase === 'finished'`. */
export interface GameOutcome {
	/** Winner user_id, or undefined for a tie. */
	readonly winner?: string;
	readonly payouts?: ReadonlyArray<{ readonly userId: string; readonly points: number }>;
	/** Game-kind-specific final state, hex. */
	readonly finalState?: string;
}

/** The folded state of a single game session. */
export interface GameSession {
	readonly sessionId: string; // hex
	readonly gameKind: GameKind;
	readonly phase: GamePhase;
	readonly createdBy: string;
	readonly moves: ReadonlyArray<GameMove>;
	readonly outcome?: GameOutcome;
}

// ---------------------------------------------------------------------------
// Event inputs. These are the normalized, decoded game bodies the host
// feeds in from its `onMessage` (or a row replay). The host is expected
// to have already attached the sender (`by`) where the envelope itself
// doesn't carry it.
// ---------------------------------------------------------------------------

export interface GameCreateEvent {
	readonly type: "create";
	readonly sessionId: string;
	readonly gameKind: GameKind;
	readonly createdBy: string;
}

export interface GameJoinEvent {
	readonly type: "join";
	readonly sessionId: string;
	readonly by: string;
	readonly seat?: number;
}

export interface GameMoveEvent {
	readonly type: "move";
	readonly sessionId: string;
	readonly moveIdx: number;
	readonly by?: string;
	readonly payload: string;
	readonly commit?: string;
}

export interface GameRevealEvent {
	readonly type: "reveal";
	readonly sessionId: string;
	readonly moveIdx: number;
	readonly by?: string;
	/** The revealed value (hex) — recorded as the move's payload. */
	readonly value: string;
}

export interface GameOutcomeEvent {
	readonly type: "outcome";
	readonly sessionId: string;
	readonly winner?: string;
	readonly payouts?: ReadonlyArray<{ readonly userId: string; readonly points: number }>;
	readonly finalState?: string;
}

export interface GameAbortEvent {
	readonly type: "abort";
	readonly sessionId: string;
	readonly reason?: "timeout" | "leave" | "invalid_state";
}

export type GameEvent =
	| GameCreateEvent
	| GameJoinEvent
	| GameMoveEvent
	| GameRevealEvent
	| GameOutcomeEvent
	| GameAbortEvent;

// ---------------------------------------------------------------------------
// Envelope-body builders. The host wraps these as
//   conv.send({ kind: 'com.ping.game_create', payload: body })
//   conv.send({ kind: 'com.ping.game_move',   payload: body })
// The shapes match the decoders in appEvent/envelope.ts (snake_case,
// hex strings for binary fields).
// ---------------------------------------------------------------------------

/** Body for a `com.ping.game_create` envelope. */
export interface GameCreateBodyJson {
	readonly game_kind: GameKind;
	readonly session_id: string; // hex
	readonly params: Record<string, unknown>;
	readonly stakes?: { readonly kind: "leaderboard" | "none" };
}

/** Body for a `com.ping.game_move` envelope. */
export interface GameMoveBodyJson {
	readonly session_id: string; // hex
	readonly move_idx: number;
	readonly payload: string; // hex
	readonly commit?: string; // hex
}

// ---------------------------------------------------------------------------
// Hex / id helpers.
// ---------------------------------------------------------------------------

function bytesToHex(bytes: Uint8Array): string {
	let out = "";
	for (let i = 0; i < bytes.length; i += 1) {
		out += bytes[i]!.toString(16).padStart(2, "0");
	}
	return out;
}

/** 16-byte random session id, hex-encoded. */
function newSessionIdHex(): string {
	const buf = new Uint8Array(16);
	// `crypto.getRandomValues` exists on web and in the RN runtimes used
	// here (react-native-get-random-values is installed app-side).
	crypto.getRandomValues(buf);
	return bytesToHex(buf);
}

// ---------------------------------------------------------------------------
// Pure reducer. Exported for direct testing.
// ---------------------------------------------------------------------------

/**
 * Folds one `GameEvent` into the session map, returning a NEW map (the
 * input is never mutated) so React sees a fresh reference. Unknown
 * session_ids on non-create events are tolerated (the create may not
 * have replayed yet) by synthesizing a minimal `open` session.
 */
export function reduceGameEvent(
	prev: ReadonlyMap<string, GameSession>,
	evt: GameEvent,
): Map<string, GameSession> {
	const next = new Map(prev);
	const existing = next.get(evt.sessionId);

	switch (evt.type) {
		case "create": {
			// Create is authoritative for gameKind/createdBy; later
			// creates for the same id are ignored (idempotent replay).
			if (existing) return prev as Map<string, GameSession>;
			next.set(evt.sessionId, {
				sessionId: evt.sessionId,
				gameKind: evt.gameKind,
				phase: "open",
				createdBy: evt.createdBy,
				moves: [],
			});
			return next;
		}
		case "join": {
			const base = existing ?? synthesize(evt.sessionId);
			// A join doesn't add a move; it flips an `open` game into
			// `in_progress` once a second player is present.
			next.set(evt.sessionId, {
				...base,
				phase: base.phase === "open" ? "in_progress" : base.phase,
			});
			return next;
		}
		case "move":
		case "reveal": {
			const base = existing ?? synthesize(evt.sessionId);
			const payload = evt.type === "move" ? evt.payload : evt.value;
			const move: GameMove = {
				moveIdx: evt.moveIdx,
				payload,
				...(evt.by !== undefined ? { by: evt.by } : {}),
				...(evt.type === "move" && evt.commit !== undefined
					? { commit: evt.commit }
					: {}),
			};
			// De-dupe by moveIdx so replays don't double-append; a reveal
			// for an existing committed move replaces it (fills in payload).
			const moves = upsertMove(base.moves, move);
			next.set(evt.sessionId, {
				...base,
				phase: base.phase === "finished" || base.phase === "aborted"
					? base.phase
					: "in_progress",
				moves,
			});
			return next;
		}
		case "outcome": {
			const base = existing ?? synthesize(evt.sessionId);
			const outcome: GameOutcome = {
				...(evt.winner !== undefined ? { winner: evt.winner } : {}),
				...(evt.payouts !== undefined ? { payouts: evt.payouts } : {}),
				...(evt.finalState !== undefined ? { finalState: evt.finalState } : {}),
			};
			next.set(evt.sessionId, { ...base, phase: "finished", outcome });
			return next;
		}
		case "abort": {
			const base = existing ?? synthesize(evt.sessionId);
			// An abort is terminal unless the game already finished.
			if (base.phase === "finished") return prev as Map<string, GameSession>;
			next.set(evt.sessionId, { ...base, phase: "aborted" });
			return next;
		}
		default: {
			const _exhaustive: never = evt;
			void _exhaustive;
			return prev as Map<string, GameSession>;
		}
	}
}

function synthesize(sessionId: string): GameSession {
	return {
		sessionId,
		gameKind: "coinflip",
		phase: "open",
		createdBy: "",
		moves: [],
	};
}

function upsertMove(
	moves: ReadonlyArray<GameMove>,
	move: GameMove,
): ReadonlyArray<GameMove> {
	const idx = moves.findIndex((m) => m.moveIdx === move.moveIdx);
	if (idx === -1) {
		// Keep moves sorted by moveIdx for deterministic rendering.
		const out = [...moves, move];
		out.sort((a, b) => a.moveIdx - b.moveIdx);
		return out;
	}
	const out = moves.slice();
	out[idx] = move;
	return out;
}

// ---------------------------------------------------------------------------
// Shared decode: message rows → ordered GameEvent[].
//
// Folds a list of persisted/decoded message rows into the ordered game
// event stream `replaceFromEvents` expects. Extracted here so every host
// (main chat + thread panel) derives sessions the same way instead of
// duplicating the parse/attribute logic. The caller is expected to pass
// rows in chronological order; `by`/`createdBy` is attributed from the
// row's sender (`'__self__'` for our own rows so it matches the
// optimistic folds the host applies on send).
// ---------------------------------------------------------------------------

const GAME_KINDS: ReadonlySet<string> = new Set([
	"com.ping.game_create",
	"com.ping.game_join",
	"com.ping.game_move",
	"com.ping.game_reveal",
	"com.ping.game_outcome",
	"com.ping.game_abort",
]);

/** True for any `com.ping.game_*` envelope kind. */
export function isGameKind(kind: string): boolean {
	return GAME_KINDS.has(kind);
}

/**
 * Map one decoded `com.ping.game_*` envelope body to a `GameEvent`.
 * `actor` attributes the implicit-actor wire bodies (the sender IS the
 * actor). Returns `null` for non-game / malformed bodies.
 */
export function gameEventFromBody(
	kind: string,
	body: unknown,
	actor: string,
): GameEvent | null {
	switch (kind) {
		case "com.ping.game_create": {
			const b = body as GameCreateBody;
			if (!b.session_id || b.session_id.length === 0) return null;
			return {
				type: "create",
				sessionId: b.session_id,
				gameKind: b.game_kind,
				createdBy: actor,
			};
		}
		case "com.ping.game_join": {
			const b = body as GameJoinBody;
			if (!b.session_id || b.session_id.length === 0) return null;
			return {
				type: "join",
				sessionId: b.session_id,
				by: actor,
				...(typeof b.seat === "number" ? { seat: b.seat } : {}),
			};
		}
		case "com.ping.game_move": {
			const b = body as GameMoveBody;
			if (!b.session_id || b.session_id.length === 0) return null;
			return {
				type: "move",
				sessionId: b.session_id,
				moveIdx: b.move_idx,
				payload: b.payload,
				by: actor,
				...(b.commit !== undefined ? { commit: b.commit } : {}),
			};
		}
		case "com.ping.game_reveal": {
			const b = body as GameRevealBody;
			if (!b.session_id || b.session_id.length === 0) return null;
			return {
				type: "reveal",
				sessionId: b.session_id,
				moveIdx: b.move_idx,
				value: b.value,
				by: actor,
			};
		}
		case "com.ping.game_outcome": {
			const b = body as GameOutcomeBody;
			if (!b.session_id || b.session_id.length === 0) return null;
			return {
				type: "outcome",
				sessionId: b.session_id,
				...(b.winner !== undefined ? { winner: b.winner } : {}),
				...(b.payouts !== undefined
					? {
							payouts: b.payouts.map((p) => ({
								userId: p.user_id,
								points: p.points,
							})),
						}
					: {}),
				...(b.final_state.length > 0 ? { finalState: b.final_state } : {}),
			};
		}
		case "com.ping.game_abort": {
			const b = body as GameAbortBody;
			if (!b.session_id || b.session_id.length === 0) return null;
			return { type: "abort", sessionId: b.session_id, reason: b.reason };
		}
		default:
			return null;
	}
}

/** A message-like row this module can fold into game events. */
export interface GameMessageRowLike {
	readonly body?: string | undefined;
	readonly isSelf: boolean;
	/** Sender account id — used to attribute peer events. */
	readonly senderId: string;
}

/**
 * Derive the ordered game-event stream from a chronological list of
 * message rows. Pass the result to `replaceFromEvents` so a game's
 * folded state (and thus winner) survives reload and renders live.
 */
export function gameEventsFromMessages(
	rows: readonly GameMessageRowLike[],
): GameEvent[] {
	const events: GameEvent[] = [];
	for (const m of rows) {
		if (m.body === undefined || m.body.length === 0 || m.body.charAt(0) !== "{") {
			continue;
		}
		const parsed = parseEnvelope(m.body);
		if (parsed === undefined || !isGameKind(parsed.kind) || !("body" in parsed)) {
			continue;
		}
		const actor = m.isSelf ? "__self__" : m.senderId;
		const evt = gameEventFromBody(parsed.kind, parsed.body, actor);
		if (evt !== null) events.push(evt);
	}
	return events;
}

// ---------------------------------------------------------------------------
// Hook.
// ---------------------------------------------------------------------------

export interface UseGameSessionsResult {
	/** Derived projection keyed by session_id (hex). */
	readonly sessions: ReadonlyMap<string, GameSession>;
	/** Fold a single decoded game event into the projection. */
	readonly applyGameEvent: (evt: GameEvent) => void;
	/**
	 * Replace the projection by folding an ordered event list from
	 * scratch — used to re-derive sessions from persisted messages on
	 * load so a game's outcome survives a reload.
	 */
	readonly replaceFromEvents: (events: readonly GameEvent[]) => void;
	/**
	 * Build the body for a `com.ping.game_create` envelope and the fresh
	 * session id. The host should `conv.send` the body, then (optimistically)
	 * `applyGameEvent({ type: 'create', ... })` for the local player.
	 */
	readonly createGame: (
		kind: GameKind,
		opts?: {
			readonly params?: Record<string, unknown>;
			readonly stakes?: { readonly kind: "leaderboard" | "none" };
		},
	) => { readonly sessionId: string; readonly body: GameCreateBodyJson };
	/** Build the body for a `com.ping.game_move` envelope. */
	readonly makeMove: (
		sessionId: string,
		payload: { readonly moveIdx: number; readonly payload: string; readonly commit?: string },
	) => GameMoveBodyJson;
}

/**
 * `useGameSessions(conversationId)` — keeps an in-memory projection of
 * game sessions for one conversation. The projection resets when the
 * conversation id changes (a different room has a disjoint session set).
 */
export function useGameSessions(conversationId: string): UseGameSessionsResult {
	const [sessions, setSessions] = useState<ReadonlyMap<string, GameSession>>(
		() => new Map(),
	);

	// Reset the projection when the conversation changes. Tracked via a
	// ref + lazy compare so we don't need an effect (keeps the map in
	// sync within the same render the id flips).
	const convRef = useRef(conversationId);
	if (convRef.current !== conversationId) {
		convRef.current = conversationId;
		// Schedule a reset; setState during render with a new identity is
		// safe here because it's guarded by the id change.
		if (sessions.size > 0) {
			setSessions(new Map());
		}
	}

	const applyGameEvent = useCallback((evt: GameEvent) => {
		setSessions((prev) => reduceGameEvent(prev, evt));
	}, []);

	// Rebuild the ENTIRE projection from an ordered event list. Used by
	// hosts to re-derive sessions from PERSISTED messages on load so the
	// previous game's outcome survives a reload (the in-memory fold above
	// resets to empty on every mount). Folding from scratch (not append)
	// makes the result a pure function of the persisted log — replaying
	// the same messages is idempotent, so it can run on every change.
	const replaceFromEvents = useCallback((events: readonly GameEvent[]) => {
		setSessions(events.reduce((acc, e) => reduceGameEvent(acc, e), new Map()));
	}, []);

	const createGame = useCallback<UseGameSessionsResult["createGame"]>(
		(kind, opts) => {
			const sessionId = newSessionIdHex();
			const body: GameCreateBodyJson = {
				game_kind: kind,
				session_id: sessionId,
				params: opts?.params ?? {},
				...(opts?.stakes !== undefined ? { stakes: opts.stakes } : {}),
			};
			return { sessionId, body };
		},
		[],
	);

	const makeMove = useCallback<UseGameSessionsResult["makeMove"]>(
		(sessionId, payload) => {
			const body: GameMoveBodyJson = {
				session_id: sessionId,
				move_idx: payload.moveIdx,
				payload: payload.payload,
				...(payload.commit !== undefined ? { commit: payload.commit } : {}),
			};
			return body;
		},
		[],
	);

	return useMemo(
		() => ({ sessions, applyGameEvent, replaceFromEvents, createGame, makeMove }),
		[sessions, applyGameEvent, replaceFromEvents, createGame, makeMove],
	);
}
